import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAllJobs } from "../../../data/requests/job-requests";
import { AdminLayout } from "../shared/layout";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/table";
import { Eye, Clock, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export function JobListScreen() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  
  const { data, isLoading } = useQuery({
    queryKey: ["admin-jobs", { status }],
    queryFn: () => getAllJobs({ status }).then(res => res.data),
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { variant: "secondary", icon: Clock, label: "Pending" },
      PROCESSING: { variant: "default", icon: AlertCircle, label: "Processing" },
      COMPLETED: { variant: "success", icon: CheckCircle, label: "Completed" },
      FAILED: { variant: "destructive", icon: XCircle, label: "Failed" },
      CANCELLED: { variant: "secondary", icon: XCircle, label: "Cancelled" },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant}>
        <Icon className="mr-1 h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const columns = [
    { key: "id", label: "Job ID" },
    {
      key: "user",
      label: "User",
      render: (row) => row.user?.email || "—",
    },
    {
      key: "template",
      label: "Template",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.template?.thumbnailUrl && (
            <img
              src={row.template.thumbnailUrl}
              alt={row.template.name}
              className="w-10 h-10 object-cover rounded"
            />
          )}
          <span>{row.template?.name}</span>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => getStatusBadge(row.status),
    },
    {
      key: "createdAt",
      label: "Created",
      render: (row) => new Date(row.createdAt).toLocaleString(),
    },
    {
      key: "processingTime",
      label: "Time",
      render: (row) => row.processingTime ? `${(row.processingTime / 1000).toFixed(2)}s` : "—",
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/app/jobs/${row.id}`)}
        >
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">All Jobs</h1>
          <p className="text-gray-500 mt-2">
            Monitor all user AI photo processing jobs
          </p>
        </div>

        <DataTable
          data={data?.jobs || []}
          columns={columns}
          isLoading={isLoading}
        />
      </div>
    </AdminLayout>
  );
}

